import type { CheckboxFilterOption } from './CheckboxFilterList.types'

/**
 * Pins selected options to the top. By default each partition (selected,
 * unselected) is alphabetized by label — the original behavior most callers
 * (e.g. an unordered Labels dropdown) rely on for a predictable list.
 *
 * Pass `preserveOrder: true` when the caller has already relevance-ranked
 * the options itself (e.g. facility search's name/short-name-before-address
 * ordering, see getIncidentFacilityOptionsWithAddress) — alphabetizing would
 * silently destroy that ranking. An earlier version of this function always
 * re-sorted the unselected group by label with no opt-out, which broke
 * facility search; a later version dropped alphabetizing entirely, which
 * broke unrelated, non-ranked callers. `preserveOrder` lets each caller pick.
 */
export const getSortedOptions = (
  options: CheckboxFilterOption[],
  selectedValues: string[],
  preserveOrder = false
): CheckboxFilterOption[] => {
  const selected = new Set(selectedValues)
  const selectedOptions = options.filter((option) => selected.has(option.value))
  const unselectedOptions = options.filter(
    (option) => !selected.has(option.value)
  )
  if (preserveOrder) {
    return [...selectedOptions, ...unselectedOptions]
  }
  const byLabel = (a: CheckboxFilterOption, b: CheckboxFilterOption) =>
    (a.label ?? '').localeCompare(b.label ?? '')
  return [...selectedOptions.sort(byLabel), ...unselectedOptions.sort(byLabel)]
}
